import { AppInitialisation, StartParam } from '@/domain/Sharing'

/**
 * Telegram start parameters (t.me/<bot>/<app>?startapp=...), the same
 * grammar as the backend (tma-videochat internal/marketing/startparam.go;
 * both run startparam_vectors.json):
 *   - plain tokens joined by "__": cmp_<campaign>, ref_<code>, l_<slug>;
 *   - base64 (url or standard alphabet, padding optional) JSON
 *     {t: path, r: referrer public id, c: cmp, k: ref code, l: slug}.
 * Anything not understood is dropped: a bad link still opens the app.
 */
const cmpRe = /^[A-Za-z0-9-]{1,40}$/
const refCodeRe = /^[A-Za-z0-9]{4,16}$/
const slugRe = /^[a-z0-9-]{1,32}$/
const publicIdRe = /^[a-z0-9]{1,30}$/

export function parseStartParam(raw: string | undefined): StartParam {
    const value = (raw || '').trim()
    if (!value || value.length > 512) return {}
    const tokens = parseTokens(value)
    return tokens ?? parseEncoded(value)
}

function parseTokens(raw: string): StartParam | undefined {
    const p: StartParam = {}
    let found = false
    for (const tok of raw.split('__')) {
        if (tok.startsWith('cmp_') && cmpRe.test(tok.slice(4))) {
            p.cmp = tok.slice(4)
            found = true
        } else if (tok.startsWith('ref_') && refCodeRe.test(tok.slice(4))) {
            p.ref = tok.slice(4)
            found = true
        } else if (tok.startsWith('l_') && slugRe.test(tok.slice(2))) {
            p.link = tok.slice(2)
            found = true
        }
    }
    return found ? p : undefined
}

function decodeBase64(raw: string): string | undefined {
    const std = raw.replace(/=+$/, '').replace(/-/g, '+').replace(/_/g, '/')
    if (!/^[A-Za-z0-9+/]*$/.test(std) || std.length % 4 === 1) return undefined
    try {
        return atob(std + '='.repeat((4 - (std.length % 4)) % 4))
    } catch {
        return undefined
    }
}

function parseEncoded(raw: string): StartParam {
    const decoded = decodeBase64(raw)
    if (decoded === undefined) return {}
    let body: Record<string, unknown>
    try {
        body = JSON.parse(decoded)
    } catch {
        return {}
    }
    if (!body || typeof body !== 'object') return {}
    const str = (k: string) =>
        typeof body[k] === 'string' ? (body[k] as string) : ''
    const p: StartParam = {}
    const t = str('t')
    if (t.startsWith('/') && !t.startsWith('//') && t.length <= 256) p.path = t
    if (publicIdRe.test(str('r'))) p.referrer = str('r')
    if (cmpRe.test(str('c'))) p.cmp = str('c')
    if (refCodeRe.test(str('k'))) p.ref = str('k')
    if (slugRe.test(str('l'))) p.link = str('l')
    return p
}

/** The link carries something the marketing engine tracks. */
export function isMarketing(p: StartParam): boolean {
    return Boolean(p.cmp || p.ref || p.link)
}

/** What the home route should do with a parsed start parameter. */
export function toInitState(p: StartParam): AppInitialisation | undefined {
    if (!p.path && !p.ref && !p.link) return undefined
    return {
        t: p.path || '',
        r: p.referrer || '',
        ...(p.cmp ? { c: p.cmp } : {}),
        ...(p.ref ? { k: p.ref } : {}),
        ...(p.link ? { l: p.link } : {})
    }
}

/** base64url without padding: the only alphabet Telegram accepts. */
export function encodeStartParam(payload: AppInitialisation): string {
    return btoa(JSON.stringify(payload))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}
